import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../data/translations';
import { FacebookIcon, LinkedInIcon, TwitterIcon, InstagramIcon, YouTubeIcon, WhatsAppIcon } from './Icons';
import './Footer.css';

const Footer = () => {
  const { language, toggleLanguage } = useLanguage();
  const t = translations[language];
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleNavigation = (path) => {
    navigate(path);
    scrollToTop();
  };

  const footerLinks = [
    {
      title: t.quickLinks,
      links: [
        { label: t.home, path: '/' },
        { label: t.aboutUs, path: '/about' },
        { label: t.programs, path: '/programs' },
        { label: t.partnersTitle, path: '/partnerships' },
        { label: t.mediaCenter, path: '/media-center' },
        { label: t.contact, path: '/contact' },
        { label: t.getInvolved, path: '/get-involved' },
      ]
    },
    {
      title: t.contactInfo,
      links: [
        { label: 'info@mandula.org', href: 'mailto:info@mandula.org', icon: '📧' },
        { label: '+249 912 797 684', href: 'tel:+249912797684', icon: '📱' },
        { label: '+249 129 552 787', href: 'tel:+249129552787', icon: '📱' },
        { label: 'www.mandula.org', href: 'https://www.mandula.org', icon: '🌐', external: true },
      ]
    }
  ];

  return (
    <footer className="footer-modern">
      <div className="footer-container">
        {/* Main Footer Content */}
        <div className="footer-content">
          {/* Logo and Description */}
          <div className="footer-brand">
            <div className="footer-logo">
              <img src="/MRRO/w2.png" alt="MRRO Logo" />
            </div>
            <p className="footer-description">{t.footerDescription}</p>
            
            {/* Social Links */}
            <div className="footer-social">
              <a 
                href="https://www.facebook.com/mandolarelief" 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-link"
                aria-label="Facebook"
              >
                <FacebookIcon />
              </a>
              <a 
                href="https://twitter.com/mandolarelief" 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-link"
                aria-label="Twitter"
              >
                <TwitterIcon />
              </a>
              <a 
                href="https://www.instagram.com/mandolarelief" 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-link"
                aria-label="Instagram"
              >
                <InstagramIcon />
              </a>
              <a 
                href="https://linkedin.com/company/mandolarelief" 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-link"
                aria-label="LinkedIn"
              >
                <LinkedInIcon />
              </a>
              <a 
                href="https://www.youtube.com/@mandolarelief" 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-link"
                aria-label="YouTube"
              >
                <YouTubeIcon />
              </a>
              <a 
                href="https://wa.me/249912797684" 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-link"
                aria-label="WhatsApp"
              >
                <WhatsAppIcon />
              </a>
            </div>
          </div>

          {/* Footer Links Grid */}
          <div className="footer-links-grid">
            {footerLinks.map((section, i) => (
              <div key={i} className="footer-section">
                <h3 className="footer-section-title">{section.title}</h3>
                <ul className="footer-section-links">
                  {section.links.map((link, j) => (
                    <li key={j}>
                      {link.path ? (
                        <button onClick={() => handleNavigation(link.path)}>
                          <span>{link.label}</span>
                        </button>
                      ) : (
                        <a 
                          href={link.href} 
                          target={link.external ? "_blank" : undefined}
                          rel={link.external ? "noopener noreferrer" : undefined}
                        >
                          {link.icon && <span className="link-icon">{link.icon}</span>}
                          <span>{link.label}</span>
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Divider */}
        <div className="footer-divider" />

        {/* Footer Bottom */}
        <div className="footer-bottom">
          <p className="footer-copyright">
            © {currentYear} {language === 'en' 
              ? 'Mandola Relief & Reconstruction Organization (MRRO). All rights reserved.' 
              : 'منظمة المندولة للإغاثة والإعمار. جميع الحقوق محفوظة.'}
          </p>
          
          <div className="footer-actions">
            {/* Language Toggle */}
            <div className="language-toggle">
              <button 
                className={`lang-btn ${language === 'en' ? 'active' : ''}`}
                onClick={() => language !== 'en' && toggleLanguage()}
              >
                English
              </button>
              <span className="lang-separator">|</span>
              <button 
                className={`lang-btn ${language === 'ar' ? 'active' : ''}`}
                onClick={() => language !== 'ar' && toggleLanguage()}
              >
                العربية
              </button>
            </div>

            {/* Back to Top */}
            <button className="back-to-top" onClick={scrollToTop}>
              <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
              </svg>
              <span>{t.backToTop}</span>
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
